import { CircularProgress } from '@material-ui/core';
import { ClearSearchedTextIcon, GetCategoriesIcon, GetSportIcon, SearchIcon } from 'assets/icons';
import { NoEventsFound } from 'assets/icons/NoEventsFound';
import cn from 'classnames';
import { t } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { createRef, FC, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import AnimatedPlaceholderInput from 'shared/components/AnimatedPlaceholderInput';
import { SearchEvents, setDataLayer } from 'shared/constants/datalayer';
import useSearchBar from 'shared/hooks/useSearchbar';
import { highlightSubstring } from 'shared/util/utility';
import './styles.scss';

interface Props {
	handleCloseBottomDrawer: () => void;
	isDrawerOpen: boolean;
}

const Searchbar: FC<Props> = ({ handleCloseBottomDrawer, isDrawerOpen }) => {
	const { showSearch, setShowSearch, handleSearchInput, closeSearchModal, searchList, eventUrl, searchValue, loading, setSearchValue } =
		useSearchBar();
	let inputRef: React.RefObject<HTMLInputElement> | null = createRef<HTMLInputElement>();

	const eventClick = () => {
		handleCloseBottomDrawer();
		setDataLayer(SearchEvents.DATALAYER_SEARCH_SUCCESS);
	};

	const clearInputField = () => {
		setSearchValue('');
		setDataLayer(SearchEvents.DATALAYER_SEARCH_CANCEL(searchValue));
	};

	const getIsCategoryLevel = (categoryLevel?: number) => {
		if (!categoryLevel) return false;
		if (categoryLevel === 1 || categoryLevel === 2) return true;
	};

	useEffect(() => {
		if (!isDrawerOpen) {
			closeSearchModal();
		} else {
			inputRef?.current?.focus();
		}
	}, [isDrawerOpen]);

	useEffect(() => {
		const handlePopState = () => {
			if (isDrawerOpen) {
				handleCloseBottomDrawer();
			}
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [isDrawerOpen, showSearch]);

	return (
		<>
			<div
				className="Searchbar"
				onClick={(e) => e.stopPropagation()}
				style={{ overflow: !isDrawerOpen ? 'hidden' : 'auto', overscrollBehaviorY: 'none' }}
			>
				<div className="Searchbar__wrapper">
					<SearchIcon className="Searchbar__searchIcon" />
					<div className="Searchbar__inputField">
						<AnimatedPlaceholderInput
							ref={(ref) => (inputRef = ref)}
							value={searchValue}
							delayAfterAnimation={1000}
							cyTestRef="search-input"
							onFocus={(e) => {
								e.stopPropagation();
								setShowSearch(true);
							}}
							onChange={(e) => {
								e.stopPropagation();
								handleSearchInput(e);
							}}
						/>
					</div>
					{searchValue.length > 0 && (
						<span data-cy="clear-search-input">
							<ClearSearchedTextIcon className="Searchbar__clearSearch" onClick={clearInputField} />
						</span>
					)}
				</div>
				{searchValue.length < 3 && searchValue.length > 0 && <p className="Searchbar__shortSearchValue">{t('tooShortSearchValueInfo')}</p>}

				{showSearch && !isEmpty(searchList) && (
					<div className="Searchbar__searchList">
						{searchList.map((listItem: any, index: number) => {
							const isCategoryLevel = getIsCategoryLevel(listItem.categoryLevel);

							return (
								<NavLink to={`${eventUrl(listItem)}`} key={`searchList-${index}`} onClick={eventClick}>
									<div className={`Searchbar__listItem ${isCategoryLevel && 'Searchbar__categoryItem'}`}>
										<div className="Searchbar__listItemInfo">
											<p
												className={cn(`Searchbar__eventName ${isCategoryLevel && 'width--full'}`, {
													searchBar__liveEventName: listItem.area === 'LIVE_EVENT',
												})}
											>
												{listItem.area === 'LIVE_EVENT' && <span className="Searchbar__liveEventBadge">live</span>}
												<GetSportIcon id={listItem.extras.SPORT_ID} />
												<GetCategoriesIcon
													category={{
														sportId: listItem.extras.SPORT_ID,
														parentCategory: listItem.extras.CATEGORY_ID_3,
														categoryId: listItem.extras.CATEGORY_ID_2,
													}}
												/>
												<span className="Searchbar__categoryName">{listItem.extras.CATEGORY_NAME_3}</span>
											</p>

											{!isCategoryLevel && (
												<p className="Searchbar__eventDate">
													{listItem.area === 'LIVE_EVENT'
														? `${Math.round((new Date().getTime() - listItem.eventStart) / 1000 / 60)} min`
														: moment(listItem.eventStart).local().format('DD.MM.YYYY HH:mm').toString()}
												</p>
											)}
										</div>
										<p
											className="Searchbar__searchTitle"
											dangerouslySetInnerHTML={{ __html: highlightSubstring(listItem.name, searchValue) }}
										/>
									</div>
								</NavLink>
							);
						})}
					</div>
				)}
				{searchValue.length < 3 && (
					<div className="Searchbar__eventInfo">
						<SearchIcon className="Searchbar__noSearchResults" />
						<p className="Searchbar__noSearchResultsText">{t('searchEventInfo')}</p>
					</div>
				)}

				{showSearch && loading && searchValue && searchValue.length >= 3 && isEmpty(searchList) && (
					<div className="Searchbar__loader">
						<CircularProgress color="secondary" className="Searchbar__loader" />
					</div>
				)}

				{showSearch && !loading && searchValue && searchValue.length >= 3 && isEmpty(searchList) && (
					<div className="Searchbar__eventInfo">
						<NoEventsFound className="SearchBar__noSearchResults" />
						<p className="Searchbar__noSearchResultsText">{t('noSearchResults')}</p>
						<p className="Searchbar__noSearchResultDescription">{t('noSearchResultTextTwo')}</p>
					</div>
				)}
			</div>
		</>
	);
};

export default Searchbar;
